/**
 * videoEmbed.js — v2.5.917
 *
 * Video URL detection + embed helpers. One shared utility so the Media
 * Library upload dialog, the landing page hero-video player, and any future
 * inline video renderer all agree on what a "shareable video URL" is and
 * how to embed it without exposing the source platform's UI chrome.
 *
 * Supported sources:
 *   • Google Drive     — file share links → /preview iframe (clean, autoplays)
 *   • YouTube          — watch / share / embed / youtu.be links → nocookie embed
 *   • Vimeo            — vimeo.com/ID → player.vimeo.com/video/ID embed
 *   • Direct MP4/webm  — same-origin uploaded files or any http(s) MP4 URL
 */

const GDRIVE_ID_PATTERNS = [
  /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,   // /file/d/FILE_ID/view
  /drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/,   // /open?id=FILE_ID
  /drive\.google\.com\/uc\?[^=]*id=([a-zA-Z0-9_-]+)/,// /uc?id=FILE_ID
];

const YOUTUBE_PATTERNS = [
  /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{6,})/,
  /youtu\.be\/([a-zA-Z0-9_-]{6,})/,
  /youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/,
  /youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/,
];

const VIMEO_PATTERN = /vimeo\.com\/(?:video\/)?(\d+)/;

/**
 * Classify a video URL and return normalised metadata.
 * @returns {{ kind: 'gdrive'|'youtube'|'vimeo'|'direct'|'unknown', id: string, url: string, embedUrl: string }}
 */
export function classifyVideoUrl(rawUrl = '') {
  const url = (rawUrl || '').trim();
  if (!url) return { kind: 'unknown', id: '', url: '', embedUrl: '' };

  // Google Drive
  for (const pat of GDRIVE_ID_PATTERNS) {
    const m = url.match(pat);
    if (m && m[1]) {
      const id = m[1];
      return {
        kind: 'gdrive',
        id,
        url,
        embedUrl: `https://drive.google.com/file/d/${id}/preview`,
      };
    }
  }

  // YouTube
  for (const pat of YOUTUBE_PATTERNS) {
    const m = url.match(pat);
    if (m && m[1]) {
      const id = m[1];
      // nocookie + autoplay + no cookie tracking + minimal chrome
      return {
        kind: 'youtube',
        id,
        url,
        embedUrl: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&modestbranding=1&rel=0&playsinline=1`,
      };
    }
  }

  // Vimeo
  const vm = url.match(VIMEO_PATTERN);
  if (vm && vm[1]) {
    return {
      kind: 'vimeo',
      id: vm[1],
      url,
      embedUrl: `https://player.vimeo.com/video/${vm[1]}?autoplay=1&muted=1&title=0&byline=0&portrait=0`,
    };
  }

  // Direct file (mp4/webm/mov/etc.) — same-origin uploaded file or CDN
  if (/^(https?:)?\/\//.test(url) || url.startsWith('/api/uploads/')) {
    return { kind: 'direct', id: '', url, embedUrl: url };
  }

  return { kind: 'unknown', id: '', url, embedUrl: url };
}

/**
 * Human-friendly label for the classifier output. Used in the upload dialog
 * to reassure the user "we detected a Google Drive video — it'll autoplay
 * cleanly with no Drive branding".
 */
export function describeVideoSource(kind) {
  return ({
    gdrive:  'Google Drive video · will autoplay without Drive UI',
    youtube: 'YouTube video · will autoplay muted, minimal branding',
    vimeo:   'Vimeo video · will autoplay muted, minimal chrome',
    direct:  'Direct video file · full HTML5 controls',
    unknown: 'Unrecognised URL — will fall back to a plain link',
  })[kind] || '';
}
